import { useState } from 'react';

import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { extractErrorMessage } from '@/lib/utils';

import {
  detailToDraft,
  draftSecrets,
  draftToConfig,
  draftToDto,
  draftToJson,
  emptyDraft,
  parseConnectorJson,
  validateDraft,
} from './connector-draft';
import { MappingEditor, selectClass } from './MappingEditor';
import { TestPreview } from './TestPreview';

import type { ConnectorDraft, KeyValue, SplitLevelDraft } from './connector-draft';
import type {
  ConnectorTestResult,
  CreateDataConnectorDto,
  DataConnectorConfig,
  DataConnectorDetail,
} from '@repo/shared-types';
import type { ReactNode } from 'react';

type Pagination = DataConnectorConfig['pagination'];

interface Props {
  initial?: DataConnectorDetail;
  onSubmit: (dto: CreateDataConnectorDto) => void;
  onCancel: () => void;
  onTest?: (body: {
    config: DataConnectorConfig;
    secrets?: Record<string, string>;
    connector_id?: string;
  }) => Promise<ConnectorTestResult>;
  isSaving?: boolean;
}

const Section = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <section className="space-y-3 border-b pb-4 last:border-b-0">
    <div>
      <h3 className="font-semibold">{title}</h3>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
    {children}
  </section>
);

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) => (
  <label className="block space-y-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <Input
      aria-label={label}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const KeyValueRows = ({
  rows,
  onChange,
  addLabel,
  valuePlaceholder,
}: {
  rows: KeyValue[];
  onChange: (rows: KeyValue[]) => void;
  addLabel: string;
  valuePlaceholder?: string;
}) => (
  <div className="space-y-2">
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <Input
          aria-label={`${addLabel} key`}
          placeholder="name"
          value={r.key}
          onChange={(e) =>
            onChange(rows.map((x, idx) => (idx === i ? { ...x, key: e.target.value } : x)))
          }
        />
        <Input
          aria-label={`${addLabel} value`}
          placeholder={valuePlaceholder}
          value={r.value}
          onChange={(e) =>
            onChange(rows.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)))
          }
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Remove"
          onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    ))}
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => onChange([...rows, { key: '', value: '' }])}
    >
      <Plus className="mr-1 h-4 w-4" />
      {addLabel}
    </Button>
  </div>
);

const newSplitLevel = (param: string): SplitLevelDraft => ({
  param,
  facet_params: [],
  buckets_path: '',
  value_key: 'item',
  count_key: 'count',
});

/** Create/edit a data connector: request, paging, splitting, mapping, schedule, and a live Test. */
export const ConnectorForm = ({ initial, onSubmit, onCancel, onTest, isSaving }: Props) => {
  const [draft, setDraft] = useState<ConnectorDraft>(() =>
    initial ? detailToDraft(initial) : emptyDraft(),
  );
  const [issues, setIssues] = useState<string[]>([]);
  const [testResult, setTestResult] = useState<ConnectorTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonText, setJsonText] = useState('');

  const set = (patch: Partial<ConnectorDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const setPage = (patch: Partial<Pagination>) =>
    setDraft((d) => ({ ...d, pagination: { ...d.pagination, ...patch } }));
  const setSplit = (i: number, patch: Partial<SplitLevelDraft>) =>
    set({ split: draft.split.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const num = (v: string) => Number(v) || 0;

  const savedSecretNames = draft.secrets.filter((s) => s.saved).map((s) => s.name);

  /** Parses the JSON box into the form; returns the new draft, or null (errors shown). */
  const applyJson = (): ConnectorDraft | null => {
    const r = parseConnectorJson(jsonText, savedSecretNames);
    if (!r.ok) {
      setIssues(r.errors);
      return null;
    }
    setIssues([]);
    setDraft(r.draft);
    return r.draft;
  };

  const switchMode = (next: 'form' | 'json') => {
    if (next === 'json') setJsonText(draftToJson(draft));
    else if (!applyJson()) return; // stay in JSON until it's valid
    setMode(next);
  };

  const handleSubmit = () => {
    const current = mode === 'json' ? applyJson() : draft;
    if (!current) return;
    const problems = validateDraft(current);
    setIssues(problems);
    if (problems.length === 0) onSubmit(draftToDto(current));
  };

  /** From the Test preview: add "value → to" to that field's value map ('__empty__' = empty). */
  const addTranslation = (field: string, value: string, to: string) =>
    setDraft((d) => ({
      ...d,
      mapping: d.mapping.map((m) =>
        m.target === field
          ? { ...m, value_map: { ...(m.value_map ?? {}), [value]: to === '__empty__' ? '' : to } }
          : m,
      ),
    }));

  const handleTest = async () => {
    if (!onTest) return;
    setTesting(true);
    setTestError(null);
    try {
      setTestResult(
        await onTest({
          config: draftToConfig(draft),
          secrets: draftSecrets(draft),
          connector_id: initial?.id,
        }),
      );
    } catch (error) {
      setTestResult(null);
      setTestError(extractErrorMessage(error, 'Test request failed'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-muted inline-flex rounded-md p-1">
        {(['form', 'json'] as const).map((m) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={mode === m ? 'default' : 'ghost'}
            onClick={() => mode !== m && switchMode(m)}
          >
            {m === 'form' ? 'Form' : 'JSON'}
          </Button>
        ))}
      </div>

      {mode === 'json' ? (
        <Section
          title="Connector JSON"
          hint='Paste or edit the whole connector (name, config, secrets, schedule). Secrets go in "secrets"; saved ones are never shown and are kept unless you set them to "".'
        >
          <textarea
            aria-label="Connector JSON"
            className="border-input bg-background h-[28rem] w-full rounded-md border p-3 font-mono text-xs"
            spellCheck={false}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
          />
          <Button type="button" variant="secondary" onClick={() => applyJson() && setMode('form')}>
            Apply JSON
          </Button>
        </Section>
      ) : (
        <>
          <Section title="Connector">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" value={draft.name} onChange={(v) => set({ name: v })} />
              <Field
                label="Description"
                value={draft.description}
                onChange={(v) => set({ description: v })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={draft.is_active} onCheckedChange={(v) => set({ is_active: v })} />
              Active
            </label>
          </Section>

          <Section
            title="Request"
            hint="Use {{secret.<name>}} in params or headers to insert a saved secret (e.g. api_key={{secret.api_key}})."
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Field
                label="URL"
                value={draft.url}
                onChange={(v) => set({ url: v })}
                placeholder="https://api.example.com/v2/search"
              />
              <label className="block space-y-1 text-sm">
                <span className="text-muted-foreground">Method</span>
                <select
                  aria-label="Method"
                  className={`${selectClass} w-full`}
                  value={draft.method}
                  onChange={(e) => set({ method: e.target.value as ConnectorDraft['method'] })}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>
              </label>
            </div>
            <p className="text-sm font-medium">Query params</p>
            <KeyValueRows
              rows={draft.params}
              onChange={(params) => set({ params })}
              addLabel="Add param"
              valuePlaceholder="value or {{secret.api_key}}"
            />
            <p className="text-sm font-medium">Headers</p>
            <KeyValueRows
              rows={draft.headers}
              onChange={(headers) => set({ headers })}
              addLabel="Add header"
            />
          </Section>

          <Section
            title="Secrets"
            hint="Stored encrypted and never shown again. Leave blank to keep."
          >
            {draft.secrets.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  aria-label="Secret name"
                  placeholder="api_key"
                  value={s.name}
                  disabled={s.saved}
                  onChange={(e) =>
                    set({
                      secrets: draft.secrets.map((x, idx) =>
                        idx === i ? { ...x, name: e.target.value } : x,
                      ),
                    })
                  }
                />
                <Input
                  aria-label="Secret value"
                  type="password"
                  placeholder={s.saved ? '••••• saved (type to replace)' : 'value'}
                  value={s.value}
                  onChange={(e) =>
                    set({
                      secrets: draft.secrets.map((x, idx) =>
                        idx === i ? { ...x, value: e.target.value } : x,
                      ),
                    })
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Remove secret"
                  onClick={() =>
                    set({
                      secrets: draft.secrets.filter((_, idx) => idx !== i),
                      removedSecrets: s.saved
                        ? [...draft.removedSecrets, s.name]
                        : draft.removedSecrets,
                    })
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                set({ secrets: [...draft.secrets, { name: '', value: '', saved: false }] })
              }
            >
              <Plus className="mr-1 h-4 w-4" />
              Add secret
            </Button>
          </Section>

          <Section title="Response">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Records path"
                value={draft.records_path}
                onChange={(v) => set({ records_path: v })}
                placeholder="listings"
              />
              <Field
                label="Total path"
                value={draft.total_path}
                onChange={(v) => set({ total_path: v })}
                placeholder="num_found"
              />
            </div>
          </Section>

          <Section
            title="Pagination"
            hint="Max depth = the deepest offset the API allows (e.g. 15000). Larger catalogues need splitting."
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block space-y-1 text-sm">
                <span className="text-muted-foreground">Mode</span>
                <select
                  aria-label="Pagination mode"
                  className={`${selectClass} w-full`}
                  value={draft.pagination.mode}
                  onChange={(e) => setPage({ mode: e.target.value as Pagination['mode'] })}
                >
                  <option value="offset">Offset (start + rows)</option>
                  <option value="page">Page number</option>
                </select>
              </label>
              <Field
                label={draft.pagination.mode === 'offset' ? 'Offset param' : 'Page param'}
                value={draft.pagination.param}
                onChange={(v) => setPage({ param: v })}
              />
              <Field
                label="Page size param"
                value={draft.pagination.size_param}
                onChange={(v) => setPage({ size_param: v })}
              />
              <Field
                label="Page size"
                type="number"
                value={draft.pagination.page_size}
                onChange={(v) => setPage({ page_size: num(v) })}
              />
              <Field
                label="Max depth"
                type="number"
                value={draft.pagination.max_depth}
                onChange={(v) => setPage({ max_depth: num(v) })}
              />
              <Field
                label="Delay between requests (ms)"
                type="number"
                value={draft.pagination.delay_ms}
                onChange={(v) => setPage({ delay_ms: num(v) })}
              />
              {draft.pagination.mode === 'page' && (
                <Field
                  label="First page number"
                  type="number"
                  value={draft.pagination.first_page}
                  onChange={(v) => setPage({ first_page: num(v) === 0 ? 0 : 1 })}
                />
              )}
            </div>
          </Section>

          <Section
            title="Splitting"
            hint="Split by a field (e.g. make); groups still over max depth are split again by the second field (e.g. model)."
          >
            {draft.split.map((s, i) => (
              <div key={i} className="space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Level {i + 1}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Remove level"
                    onClick={() => set({ split: draft.split.filter((_, idx) => idx !== i) })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label="Split param"
                    value={s.param}
                    onChange={(v) => setSplit(i, { param: v })}
                    placeholder="make"
                  />
                  <Field
                    label="Buckets path"
                    value={s.buckets_path}
                    onChange={(v) => setSplit(i, { buckets_path: v })}
                    placeholder="facets.make"
                  />
                  <Field
                    label="Value key"
                    value={s.value_key}
                    onChange={(v) => setSplit(i, { value_key: v })}
                  />
                  <Field
                    label="Count key"
                    value={s.count_key}
                    onChange={(v) => setSplit(i, { count_key: v })}
                  />
                </div>
                <p className="text-sm font-medium">Count request params</p>
                <KeyValueRows
                  rows={s.facet_params}
                  onChange={(facet_params) => setSplit(i, { facet_params })}
                  addLabel="Add count param"
                  valuePlaceholder="e.g. rows=0, facets=make|0|1000"
                />
              </div>
            ))}
            {draft.split.length < 2 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  set({
                    split: [...draft.split, newSplitLevel(draft.split.length ? 'model' : 'make')],
                  })
                }
              >
                <Plus className="mr-1 h-4 w-4" />
                Add split level
              </Button>
            )}
          </Section>

          <Section
            title="Field mapping"
            hint="Choose where each response field is saved. Extra fields go under custom.<name> and are searchable as name:value."
          >
            <MappingEditor
              value={draft.mapping}
              onChange={(mapping) => set({ mapping })}
              paths={testResult?.paths ?? []}
            />
          </Section>

          <Section title="Schedule">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={draft.schedule_enabled}
                onCheckedChange={(v) => set({ schedule_enabled: v })}
              />
              Sync automatically
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Every N days (7 = weekly)"
                type="number"
                value={draft.schedule_interval_days}
                onChange={(v) => set({ schedule_interval_days: num(v) })}
              />
              <Field
                label="At time (HH:mm)"
                type="time"
                value={draft.schedule_time}
                onChange={(v) => set({ schedule_time: v })}
              />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Switch
                checked={draft.auto_retire}
                onCheckedChange={(v) => set({ auto_retire: v })}
              />
              <span>
                Replace unlisted cars automatically
                <span className="text-muted-foreground block text-xs">
                  Each run marks cars the source no longer lists as unavailable without asking. Off:
                  you&apos;re asked when over 20% would go. Either way, a run that would remove over
                  half the cars still asks (usually a provider problem).
                </span>
              </span>
            </label>
          </Section>
        </>
      )}

      {onTest && mode === 'form' && (
        <Section title="Test" hint="Fetches one page with the settings above (nothing is saved).">
          <Button type="button" variant="secondary" onClick={handleTest} disabled={testing}>
            {testing ? 'Testing…' : 'Test'}
          </Button>
          {testError && <p className="text-destructive text-sm">{testError}</p>}
          {testResult && <TestPreview result={testResult} onTranslate={addTranslation} />}
        </Section>
      )}

      {issues.length > 0 && (
        <ul className="text-destructive list-disc space-y-1 pl-5 text-sm">
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save connector'}
        </Button>
      </div>
    </div>
  );
};
